/* Falco Racing — fiche produit : médias, plein écran, configuration, barre d'achat. */
const formatMoney = (...a) => window.Falco.formatMoney(...a);
const lockScroll = (on) => window.Falco.lockScroll(on);

/* ------------------------------------------------------------------ */
/* Médias : onglets, photos au ratio d'origine, vignettes, plein écran   */
/* ------------------------------------------------------------------ */
class FalcoMedia extends HTMLElement {
  connectedCallback() {
    this.shots = [...this.querySelectorAll("[data-shot]")];
    this.frame = this.querySelector("[data-frame]");
    this.tab = this.dataset.default || "photos";
    this.i = 0;
    this.querySelectorAll("[data-tab]").forEach((b) => b.addEventListener("click", () => this.setTab(b.dataset.tab)));
    this.querySelector("[data-prev]")?.addEventListener("click", () => this.go(this.i - 1));
    this.querySelector("[data-next]")?.addEventListener("click", () => this.go(this.i + 1));
    this.querySelectorAll("[data-goto]").forEach((b) => b.addEventListener("click", () => { this.setTab("photos"); this.go(Number(b.dataset.goto)); }));
    this.shots.forEach((s) => s.addEventListener("click", () => this.openLightbox()));
    // Glisser au doigt : touch-action pan-y laisse le défilement vertical au navigateur et nous laisse le geste
    // horizontal (sans lui, le navigateur annule le geste et la photo ne change pas). Un glissement n'ouvre pas le zoom.
    let start = null, glisse = false;
    if (this.frame) this.frame.style.touchAction = "pan-y";
    this.frame?.addEventListener("pointerdown", (e) => { if (e.pointerType !== "mouse") start = { x: e.clientX, y: e.clientY }; });
    this.frame?.addEventListener("pointercancel", () => { start = null; });
    this.frame?.addEventListener("pointerup", (e) => {
      if (!start) return; const dx = e.clientX - start.x, dy = e.clientY - start.y; start = null;
      if (Math.abs(dx) < 45 || Math.abs(dx) < Math.abs(dy)) return;
      glisse = true; setTimeout(() => { glisse = false; }, 350);
      this.go(this.i + (dx < 0 ? 1 : -1));
    });
    this.frame?.addEventListener("click", (e) => { if (glisse) { e.stopPropagation(); e.preventDefault(); } }, true);
    this.sync();
  }
  setTab(t) {
    this.tab = t;
    this.querySelectorAll("[data-tab]").forEach((b) => b.setAttribute("aria-selected", String(b.dataset.tab === t)));
    this.querySelectorAll("[data-panel]").forEach((p) => (p.hidden = p.dataset.panel !== t));
    this.sync();
  }
  go(n) { this.i = Math.min(this.shots.length - 1, Math.max(0, n)); this.sync(); }
  sync() {
    const shot = this.shots[this.i];
    if (shot && this.frame) {
      const ratio = shot.dataset.ratio || 1;
      this.frame.style.aspectRatio = ratio; this.frame.style.width = `min(100%, calc(80vh * ${ratio}))`;
    }
    this.shots.forEach((s, k) => {
      const on = k === this.i;
      s.classList.toggle("opacity-100", on); s.classList.toggle("opacity-0", !on); s.classList.toggle("pointer-events-none", !on);
      s.tabIndex = on ? 0 : -1; s.setAttribute("aria-hidden", String(!on));
    });
    const prev = this.querySelector("[data-prev]"), next = this.querySelector("[data-next]");
    if (prev) prev.disabled = this.i === 0; if (next) next.disabled = this.i === this.shots.length - 1;
    const c = this.querySelector("[data-counter]"); if (c) c.textContent = `${this.i + 1} / ${this.shots.length}`;
    this.querySelectorAll("[data-goto]").forEach((b) => b.setAttribute("aria-current", String(this.tab === "photos" && Number(b.dataset.goto) === this.i)));
    this.centerThumb();
  }
  /* Le rail de vignettes est placé sous le visuel, hors des panneaux : un scrollIntoView dessus fait
     descendre la page entière à chaque changement de vue, y compris au passage sur la 3D. On ne
     déplace donc que le rail, horizontalement, sans jamais toucher au défilement de la page. */
  centerThumb() {
    const t = this.querySelector(`[data-thumb="${this.i}"]`), rail = this.querySelector("[data-thumbs]");
    if (!t || !rail || rail.scrollWidth <= rail.clientWidth) return;
    const tr = t.getBoundingClientRect(), rr = rail.getBoundingClientRect();
    rail.scrollTo({ left: rail.scrollLeft + (tr.left - rr.left) - (rr.width - tr.width) / 2, behavior: "smooth" });
  }
  openLightbox() {
    const tpl = this.querySelector("[data-lightbox]"); if (!tpl) return;
    const lb = tpl.content.firstElementChild.cloneNode(true);
    document.body.appendChild(lb);
    new Lightbox(lb, this.shots.map((s) => ({ src: s.dataset.src, ratio: Number(s.dataset.ratio) || 1 })), this.i, (n) => this.go(n));
  }
}
customElements.define("falco-media", FalcoMedia);

// Carrousel de l'onglet Vidéos : défilement natif (glisser au doigt), flèches, compteur, pause des vidéos sorties du cadre.
class FalcoVideoRail extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector("[data-vtrack]");
    this.slides = [...this.querySelectorAll("[data-vslide]")];
    if (!this.track || this.slides.length < 2) return;
    this.prev = this.querySelector("[data-vprev]");
    this.next = this.querySelector("[data-vnext]");
    this.counter = this.querySelector("[data-vcounter]");
    this.prev?.addEventListener("click", () => this.go(this.i - 1));
    this.next?.addEventListener("click", () => this.go(this.i + 1));
    this.i = 0;
    // Format d'origine : une vidéo sans ratio connu le prend à la lecture de ses métadonnées.
    this.slides.forEach((s) => s.querySelector("video")?.addEventListener("loadedmetadata", (e) => {
      const v = e.target;
      if (!v.videoWidth || !v.videoHeight) return;
      s.dataset.ratio = (v.videoWidth / v.videoHeight).toFixed(4);
      if (this.slides[this.i] === s) this.cadrer();
    }));
    // Le cadre change de largeur avec le format : on recale la vidéo courante.
    new ResizeObserver(() => { if (!this.cible) this.track.scrollLeft = this.i * this.track.clientWidth; }).observe(this.track);
    // Glisser au doigt : l'état suit la position une fois le défilement posé.
    const poser = () => { if (!this.cible) this.sync(); };
    this.track.addEventListener("scrollend", () => { this.cible = null; this.sync(); });
    this.track.addEventListener("scroll", () => {
      clearTimeout(this.t);
      this.t = setTimeout(() => { this.cible = null; poser(); }, 150);
    }, { passive: true });
    this.sync();
  }
  go(n) {
    const k = Math.max(0, Math.min(this.slides.length - 1, n));
    // Flèches : l'état change tout de suite, sans attendre la fin de l'animation.
    this.cible = true;
    this.i = k;
    this.render();
    this.track.scrollTo({ left: k * this.track.clientWidth, behavior: "smooth" });
  }
  sync() {
    const w = this.track.clientWidth || 1;
    this.i = Math.round(this.track.scrollLeft / w);
    this.render();
    this.cadrer();
  }
  // Le cadre prend le format de la vidéo affichée, une fois le défilement posé.
  cadrer() {
    const r = parseFloat(this.slides[this.i]?.dataset.ratio) || 1.7778;
    this.style.aspectRatio = r;
    this.style.width = `min(100%, calc(80vh * ${r}))`;
  }
  render() {
    this.slides.forEach((s, k) => {
      if (k !== this.i) s.querySelectorAll("video").forEach((v) => v.pause());
      s.setAttribute("aria-hidden", k === this.i ? "false" : "true");
    });
    if (this.counter) this.counter.textContent = `${this.i + 1} / ${this.slides.length}`;
    if (this.prev) this.prev.disabled = this.i === 0;
    if (this.next) this.next.disabled = this.i === this.slides.length - 1;
  }
}
customElements.define("falco-video-rail", FalcoVideoRail);

class Lightbox {
  constructor(el, images, index, onIndex) {
    this.el = el; this.images = images; this.i = index; this.onIndex = onIndex;
    this.zoom = 1; this.pos = { x: 0, y: 0 };
    this.frame = el.querySelector("[data-lb-frame]"); this.img = el.querySelector("[data-lb-img]");
    el.querySelectorAll("[data-lb-close]").forEach((b) => b.addEventListener("click", () => this.close()));
    el.querySelector("[data-lb-in]").addEventListener("click", () => this.setZoom(this.zoom * 1.4));
    el.querySelector("[data-lb-out]").addEventListener("click", () => this.setZoom(this.zoom / 1.4));
    el.querySelector("[data-lb-prev]").addEventListener("click", () => this.go(this.i - 1));
    el.querySelector("[data-lb-next]").addEventListener("click", () => this.go(this.i + 1));
    el.querySelectorAll("[data-lb-goto]").forEach((b) => b.addEventListener("click", () => this.go(Number(b.dataset.lbGoto))));
    this.onKey = (e) => {
      if (e.key === "Escape") this.close();
      if (e.key === "ArrowRight") this.go(this.i + 1);
      if (e.key === "ArrowLeft") this.go(this.i - 1);
      if (e.key === "+" || e.key === "=") this.setZoom(this.zoom * 1.4);
      if (e.key === "-") this.setZoom(this.zoom / 1.4);
    };
    window.addEventListener("keydown", this.onKey);
    const stage = el.querySelector("[data-lb-stage]");
    stage.addEventListener("wheel", (e) => { e.preventDefault(); this.setZoom(this.zoom * (e.deltaY > 0 ? 0.88 : 1.14)); }, { passive: false });
    stage.addEventListener("dblclick", () => (this.zoom > 1 ? this.reset() : this.setZoom(2.2)));
    const pointers = new Map(); let depart = null, ecart = 0;
    stage.addEventListener("pointerdown", (e) => {
      e.target.setPointerCapture?.(e.pointerId); pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 1) depart = { x: e.clientX, y: e.clientY, pos: { ...this.pos } };
      if (pointers.size === 2) { const [a, b] = [...pointers.values()]; ecart = Math.hypot(a.x - b.x, a.y - b.y); }
    });
    stage.addEventListener("pointermove", (e) => {
      if (!pointers.has(e.pointerId)) return; pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) { const [a, b] = [...pointers.values()]; const d = Math.hypot(a.x - b.x, a.y - b.y); if (ecart) this.setZoom(this.zoom * (d / ecart)); ecart = d; return; }
      if (this.zoom > 1 && depart) { this.pos = { x: depart.pos.x + (e.clientX - depart.x), y: depart.pos.y + (e.clientY - depart.y) }; this.render(); }
    });
    const up = (e) => {
      const d = depart; pointers.delete(e.pointerId); if (pointers.size === 0) ecart = 0;
      if (!d || this.zoom > 1) return; depart = null;
      const dx = e.clientX - d.x, dy = e.clientY - d.y;
      if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) this.go(this.i + (dx < 0 ? 1 : -1));
    };
    stage.addEventListener("pointerup", up); stage.addEventListener("pointercancel", up);
    lockScroll(true); this.render(); el.querySelector("[data-lb-close]:not([tabindex='-1'])")?.focus();
  }
  setZoom(z) { this.zoom = Math.min(4, Math.max(1, z)); if (this.zoom === 1) this.pos = { x: 0, y: 0 }; this.render(); }
  reset() { this.zoom = 1; this.pos = { x: 0, y: 0 }; this.render(); }
  go(n) { if (n < 0 || n > this.images.length - 1) return; this.i = n; this.reset(); this.onIndex?.(n); }
  render() {
    const im = this.images[this.i];
    this.img.src = im.src; this.frame.style.aspectRatio = im.ratio;
    this.frame.style.transform = `translate3d(${this.pos.x}px, ${this.pos.y}px, 0) scale(${this.zoom})`;
    this.frame.style.cursor = this.zoom > 1 ? "grab" : "zoom-in";
    this.el.querySelector("[data-lb-counter]").textContent = `${this.i + 1} / ${this.images.length}`;
    this.el.querySelector("[data-lb-zoom]").textContent = `${Math.round(this.zoom * 100)} %`;
    this.el.querySelector("[data-lb-in]").disabled = this.zoom >= 4; this.el.querySelector("[data-lb-out]").disabled = this.zoom <= 1;
    this.el.querySelector("[data-lb-prev]").disabled = this.i === 0; this.el.querySelector("[data-lb-next]").disabled = this.i === this.images.length - 1;
    this.el.querySelectorAll("[data-lb-goto]").forEach((b) => b.setAttribute("aria-current", String(Number(b.dataset.lbGoto) === this.i)));
  }
  close() { window.removeEventListener("keydown", this.onKey); lockScroll(false); this.el.remove(); }
}

/* ------------------------------------------------------------------ */
/* Fiche : configuration, prix, délai, 3D, barre collante               */
/* ------------------------------------------------------------------ */
class FalcoProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form[data-ajax-cart]");
    if (!this.form) return;
    this.groups = [...this.querySelectorAll("[data-group]")];
    this.qty = 1; this.added = false;
    this.basePrice = Number(this.dataset.price) || 0;
    this.key = `falco-config:${this.dataset.handle}`;
    this.wheel = this.querySelector("falco-wheel-3d");
    customElements.whenDefined("falco-wheel-3d").then(() => this.apply3d());
    this.purgeInjected();
    new MutationObserver(() => this.purgeInjected()).observe(this.form, { childList: true, subtree: true });

    this.groups.forEach((g) => {
      if (g.dataset.kind === "swatch") g.querySelectorAll("[data-choice]").forEach((b) => b.addEventListener("click", () => { this.pickSwatch(g, b); this.showWheel(); }));
      else if (g.dataset.kind === "option") {
        g.querySelector("[data-option-toggle]").addEventListener("change", () => { this.toggleOption(g); this.changed(); });
        const file = g.querySelector("[data-option-file]");
        file?.addEventListener("change", () => { this.previewFile(g); this.changed(); });
        g.querySelector("[data-preview-change]")?.addEventListener("click", () => file?.click());
        g.querySelector("[data-preview-remove]")?.addEventListener("click", () => { file.value = ""; this.previewFile(g); this.changed(); file.focus(); });
      }
      else g.querySelector("[data-value]")?.addEventListener("input", () => this.changed());
    });
    this.querySelector("[data-variant-select]")?.addEventListener("change", (e) => {
      const o = e.target.selectedOptions[0];
      // Attributs propres aux options (data-v-*) : « [data-price] » / « [data-compare] » désignent les prix affichés,
      // que changed() réécrit ; partagés avec les <option>, ils écrasaient le libellé de chaque variante.
      this.basePrice = Number(o.dataset.vPrice) || 0;
      this.querySelector("[data-variant-id]").value = o.value;
      const cmp = Number(o.dataset.vCompare) || 0, cEl = this.querySelector("[data-compare]"), dEl = this.querySelector("[data-discount]");
      if (cEl) { cEl.hidden = !(cmp > this.basePrice); cEl.textContent = formatMoney(cmp); }
      if (dEl) { dEl.hidden = !(cmp > this.basePrice); if (cmp > this.basePrice) dEl.textContent = `−${Math.round((1 - this.basePrice / cmp) * 100)} %`; }
      this.changed();
    });
    this.querySelectorAll("[data-qty]").forEach((b) => b.addEventListener("click", () => { this.qty = Math.max(1, this.qty + Number(b.dataset.qty)); this.changed(); }));
    this.form.addEventListener("submit", (e) => { if (this.missing().length) { e.preventDefault(); e.stopImmediatePropagation(); this.goToMissing(); } }, true);
    this.form.addEventListener("falco:added", () => this.setAdded(true));
    this.querySelector("[data-sticky-add]")?.addEventListener("click", () => this.form.requestSubmit());
    this.querySelector("[data-sticky-complete]")?.addEventListener("click", () => this.goToMissing());
    document.querySelectorAll("[data-back-to-config]").forEach((b) => b.addEventListener("click", () => {
      this.scrollIntoView({ behavior: "smooth", block: "start" });
      setTimeout(() => this.groups[0]?.querySelector("[data-value], input, select, button")?.focus({ preventScroll: true }), 700);
    }));

    // Pastille payante présélectionnée au chargement (ex. cerclage Noir Mat) : armer son supplément comme au clic.
    this.groups.filter((g) => g.dataset.kind === "swatch" && "addonId" in g.dataset).forEach((g) => {
      const b = g.querySelector("[data-choice][aria-pressed=true]");
      if (b) { g.dataset.addonId = b.dataset.addon || ""; g.dataset.addonActive = String(!!b.dataset.addon); }
    });
    this.restore();
    this.groups.filter((g) => g.dataset.kind === "option").forEach((g) => this.toggleOption(g));
    this.changed();
    this.stickyObserver();
  }
  pickSwatch(g, b) {
    g.querySelectorAll("[data-choice]").forEach((x) => {
      const on = x === b;
      x.setAttribute("aria-pressed", String(on));
      x.classList.toggle("ring-2", on); x.classList.toggle("ring-graphite", on); x.classList.toggle("ring-offset-2", on);
      x.classList.toggle("hover:ring-2", !on); x.classList.toggle("hover:ring-hair", !on);
      x.querySelector("[data-check]").hidden = !on;
    });
    g.querySelector("[data-value]").value = b.dataset.label;
    g.querySelector("[data-current-label]").textContent = b.dataset.label;
    const extra = Number(b.dataset.extra) || 0;
    g.querySelector("[data-current-extra]").textContent = extra ? `+${formatMoney(extra * 100)}` : "";
    const dot = g.querySelector("[data-current-dot] > span");
    if (dot) {
      if (b.dataset.neo || b.dataset.multi) dot.style.background = `url(${b.dataset.neo ? window.Falco.assets.neo : window.Falco.assets.multi}) center/cover`;
      else dot.style.background = b.dataset.matte ? b.dataset.hex : `radial-gradient(120% 120% at 30% 22%, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0) 46%), ${b.dataset.hex}`;
    }
    // Supplément propre à la pastille (finition mate, NéoChrome) : le groupe arme celui de la pastille choisie.
    if ("addonId" in g.dataset) { g.dataset.addonId = b.dataset.addon || ""; g.dataset.addonActive = String(!!b.dataset.addon); }
    // Pastilles qui reprennent les variantes Shopify (Kit Handbrake) : la pastille choisie sélectionne la variante.
    if ("syncVariant" in g.dataset) {
      const sel = this.querySelector("[data-variant-select]");
      const o = sel && [...sel.options].find((x) => x.text.split(" · ")[0].trim() === b.dataset.label);
      if (o && sel.value !== o.value) { sel.value = o.value; sel.dispatchEvent(new Event("change", { bubbles: true })); }
    }
    this.changed();
  }
  /* Option facultative payante (numéro ou logo sur les moyeux) : cochée, elle révèle et active ses champs
     (désactivés, ils ne partent pas au panier) et arme son produit supplément, que falco.js ajoute au panier. */
  toggleOption(g) {
    const on = g.querySelector("[data-option-toggle]").checked;
    g.querySelector("[data-option-body]").hidden = !on;
    g.querySelectorAll("[data-option-field], [data-option-file]").forEach((f) => (f.disabled = !on));
    if (g.dataset.addonId) g.dataset.addonActive = String(on);
  }
  /* Aperçu du fichier choisi, lu sur l'appareil du client (rien n'est envoyé avant l'ajout au panier) ;
     image illisible par le navigateur (HEIC, TIFF…) ou PDF : vignette avec l'extension. */
  previewFile(g) {
    const input = g.querySelector("[data-option-file]"), box = g.querySelector("[data-option-preview]");
    if (!input || !box) return;
    const file = input.files[0], img = box.querySelector("[data-preview-img]"), ext = box.querySelector("[data-preview-ext]");
    if (this.previewUrl) { URL.revokeObjectURL(this.previewUrl); this.previewUrl = null; }
    box.hidden = !file;
    if (!file) return;
    box.querySelector("[data-preview-name]").textContent = file.name;
    const showExt = () => { img.hidden = true; ext.hidden = false; ext.textContent = (file.name.split(".").pop() || "").slice(0, 4); };
    if (!file.type.startsWith("image/")) return showExt();
    img.onerror = showExt;
    this.previewUrl = URL.createObjectURL(file);
    img.src = this.previewUrl; img.hidden = false; ext.hidden = true;
  }
  values() {
    const v = {};
    this.groups.forEach((g) => { v[g.dataset.id] = g.querySelector("[data-value]")?.value ?? ""; });
    return v;
  }
  choice(g) { return g.dataset.kind === "swatch" ? g.querySelector("[data-choice][aria-pressed=true]") : g.querySelector("[data-value]")?.selectedOptions?.[0]; }
  missing() {
    return this.groups.filter((g) => (g.dataset.kind === "text" && g.dataset.required === "true" && !g.querySelector("[data-value]").value.trim())
      || (g.dataset.kind === "option" && g.querySelector("[data-option-toggle]").checked && !g.querySelector("[data-option-file]")?.files.length));
  }
  goToMissing() {
    const g = this.missing()[0]; if (!g) return;
    const f = g.querySelector("[data-value], [data-option-file]"); f.scrollIntoView({ behavior: "smooth", block: "center" }); f.focus({ preventScroll: true });
  }
  changed() {
    this.setAdded(false);
    let extras = 0, matte = false;
    const comptes = new Set(); // même supplément choisi dans deux groupes (têtes en haut / en bas) : compté une fois
    this.groups.forEach((g) => {
      if (g.dataset.kind === "option") { if (g.querySelector("[data-option-toggle]").checked) extras += Number(g.dataset.extra) || 0; return; }
      if (g.dataset.kind === "text") return;
      const c = this.choice(g); if (!c) return;
      // Menu avec un choix payant (jante avant DRD double disque) : le groupe arme le supplément du choix retenu.
      if (g.dataset.kind === "select" && "addonId" in g.dataset) { g.dataset.addonId = c.dataset.addon || ""; g.dataset.addonActive = String(!!c.dataset.addon); }
      if (c.dataset.matte) matte = true;
      const extra = Number(c.dataset.extra) || 0;
      // Supplément déjà compté dans un groupe précédent (têtes du haut) : « inclus » plutôt qu'un second « +49,90 € ».
      const deja = !!c.dataset.addon && comptes.has(c.dataset.addon);
      const tag = g.querySelector("[data-current-extra]");
      if (tag) {
        tag.textContent = deja ? "inclus" : extra ? `+${formatMoney(extra * 100)}` : "";
        tag.classList.toggle("text-gris", deja); tag.classList.toggle("text-violet", !deja);
      }
      if (deja) return;
      if (c.dataset.addon) comptes.add(c.dataset.addon);
      extras += extra;
    });
    const unit = this.basePrice + Math.round(extras * 100);
    const total = unit * this.qty;
    const set = (sel, txt) => this.querySelectorAll(sel).forEach((el) => (el.textContent = txt));
    set("[data-price]", formatMoney(this.basePrice));
    set("[data-total-base]", formatMoney(this.basePrice));
    set("[data-total-options]", formatMoney(Math.round(extras * 100)));
    set("[data-total]", formatMoney(total));
    set("[data-sticky-total]", formatMoney(total));
    set("[data-installment]", formatMoney(Math.round(total / 4)));
    set("[data-qty-label]", String(this.qty));
    const sq = this.querySelector("[data-sticky-qty]"); if (sq) { sq.hidden = this.qty <= 1; sq.textContent = `×${this.qty}`; }
    this.querySelector("[data-quantity]").value = this.qty;
    const minus = this.querySelector("[data-qty='-1']"); if (minus) minus.disabled = this.qty === 1;

    if (this.dataset.stock24h !== "true" && this.groups.some((g) => g.dataset.kind === "swatch")) {
      set("[data-delay-short]", matte ? this.dataset.delayMatteShort : this.dataset.delayDefaultShort);
      set("[data-delay-long]", matte ? this.dataset.delayMatteLong : this.dataset.delayDefaultLong);
    }

    const miss = this.missing();
    const submit = this.querySelector("[data-submit]");
    submit?.setAttribute("aria-disabled", String(miss.length > 0));
    const missEl = this.querySelector("[data-missing]");
    if (missEl) {
      missEl.hidden = miss.length === 0;
      missEl.textContent = window.Falco.strings.fill.replace("{{ fields }}", miss.map((g) => g.dataset.label.toLowerCase()).join(" et "));
    }
    const sc = this.querySelector("[data-sticky-complete]"), sa = this.querySelector("[data-sticky-add]");
    if (sc && sa) { sc.hidden = miss.length === 0; sa.hidden = miss.length > 0; }

    try { sessionStorage.setItem(this.key, JSON.stringify(this.values())); } catch {}
    this.apply3d();
  }
  restore() {
    let memo; try { memo = JSON.parse(sessionStorage.getItem(this.key) || "null"); } catch { return; }
    if (!memo) return;
    this.groups.forEach((g) => {
      const v = memo[g.dataset.id]; if (v == null || g.dataset.kind === "option") return;
      if (g.dataset.kind === "swatch") { const b = [...g.querySelectorAll("[data-choice]")].find((x) => x.dataset.label === v); if (b) this.pickSwatch(g, b); }
      else { const f = g.querySelector("[data-value]"); if (f.tagName === "SELECT" && ![...f.options].some((o) => o.value === v)) return; f.value = v; }
    });
  }
  /* Les applications d'options (Infinite Options, Customizery…) injectent leurs propres champs dans le formulaire ;
     le configurateur du thème les remplace, on les retire pour ne pas envoyer de propriétés en double. */
  purgeInjected() {
    this.form.querySelectorAll('#infiniteoptions-container, [id^="infiniteoptions"], [class*="spb-"], .customizery, [data-customizery]').forEach((n) => n.remove());
  }
  apply3d() {
    if (!this.wheel || typeof this.wheel.apply !== "function") return;
    const fin = (id) => { const g = this.groups.find((x) => x.dataset.id === id); const c = g && this.choice(g); return c ? { hex: (c.dataset.hex || "").trim() || null, name: c.dataset.label, neo: !!c.dataset.neo, multi: !!c.dataset.multi } : undefined; };
    const gradient = this.dataset.gradient === "true";
    const bicolore = this.dataset.bicolore === "true";
    const sel = this.wheel.selection || {};
    const next = gradient || bicolore
      ? { ...sel, cerclage_bas: fin("cerclage-g") || sel.cerclage_bas, cerclage_haut: fin("cerclage-d") || sel.cerclage_haut, tetes_basses: fin("tetes-g") || sel.tetes_basses, tetes_hautes: fin("tetes-d") || sel.tetes_hautes, rayons: fin("rayons") || sel.rayons, moyeux: fin("moyeux") || sel.moyeux }
      : { ...sel, cerclage_uni: fin("cerclages") || sel.cerclage_uni, rayons: fin("rayons") || sel.rayons, tetes: fin("tetes") || sel.tetes, moyeux: fin("moyeux") || sel.moyeux };
    this.wheel.apply(gradient ? "degrade-fade" : bicolore ? "bicolore" : "uni", next);
  }
  /* Une couleur choisie ne se voit que sur le rendu 3D : au clic sur une pastille, on bascule le bloc
     média dessus. Appelé depuis l'écouteur de clic et jamais depuis restore(), sinon la fiche s'ouvrirait
     sur la 3D au chargement au lieu des photos. Sans effet si le produit n'a pas d'onglet 3D. */
  showWheel() {
    if (this.dataset.wheelOnPick !== "true") return;
    const media = this.querySelector("falco-media");
    if (!media || typeof media.setTab !== "function" || !media.querySelector('[data-tab="3d"]')) return;
    media.setTab("3d");
    /* Sur grand écran le visuel est collant, donc déjà sous les yeux. En dessous, il est en haut de
       page et les pastilles sont bien plus bas : sans remontée, la bascule ne se verrait pas. */
    if (!window.matchMedia("(min-width: 1024px)").matches) media.scrollIntoView({ behavior: "smooth", block: "start" });
  }
  setAdded(on) {
    this.added = on;
    const s = window.Falco.strings;
    const btn = this.querySelector("[data-submit]");
    if (btn) { btn.classList.toggle("bg-graphite", on); btn.classList.toggle("bg-violet", !on); btn.classList.toggle("hover:bg-violet-900", !on); btn.querySelector("[data-submit-label]").textContent = on ? s.added : s.add; btn.querySelector("[data-icon-bag]").hidden = on; btn.querySelector("[data-icon-check]").hidden = !on; }
    const sa = this.querySelector("[data-sticky-add]");
    if (sa) { sa.classList.toggle("bg-graphite", on); sa.classList.toggle("bg-violet", !on); sa.querySelector("[data-sticky-label]").textContent = on ? (s.addedShort || "Ajouté") : (s.addShort || "Ajouter"); sa.querySelector("[data-icon-bag]").hidden = on; sa.querySelector("[data-icon-check]").hidden = !on; }
  }
  stickyObserver() {
    const row = this.querySelector("[data-buy-row]"), bar = this.querySelector("[data-sticky-buy]");
    if (!row || !bar) return;
    new IntersectionObserver(([e]) => {
      const on = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.setAttribute("aria-hidden", String(!on));
      bar.classList.toggle("translate-y-0", on); bar.classList.toggle("opacity-100", on); bar.classList.toggle("translate-y-6", !on); bar.classList.toggle("opacity-0", !on);
      bar.firstElementChild.classList.toggle("pointer-events-auto", on);
      bar.querySelectorAll("button").forEach((b) => (b.tabIndex = on ? 0 : -1));
    }, { threshold: 0 }).observe(row);
  }
}
customElements.define("falco-product", FalcoProduct);

/* ------------------------------------------------------------------ */
/* Recommandations Shopify                                              */
/* ------------------------------------------------------------------ */
class FalcoRecommendations extends HTMLElement {
  connectedCallback() {
    const obs = new IntersectionObserver(([e]) => { if (!e.isIntersecting) return; obs.disconnect(); this.load(); }, { rootMargin: "300px" });
    obs.observe(this);
  }
  async load() {
    try {
      const html = await (await fetch(this.dataset.url)).text();
      const fresh = new DOMParser().parseFromString(html, "text/html").querySelector("falco-recommendations");
      if (fresh && fresh.querySelector("li")) this.replaceChildren(...fresh.childNodes);
    } catch (err) { console.warn("Recommandations indisponibles :", err); }
  }
}
customElements.define("falco-recommendations", FalcoRecommendations);
